import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useUserRoles } from '@anytag/auth';
import { usePostDetailsParam } from '@anytag/shared-hooks';
import { CampaignType, InfluencerCampaignListSortField } from '@anytag/graphql';
import { formatPrice, formatPercent, formatIntNumber, getSocialMediaTextLink, cn } from '@anytag/shared-utils';
import { Anchor, TableCell, TextCutter, SocialIcon, MediaPreview, SocialIconProps } from '@anytag/shared-atoms';
import { InfluencerProfileCampaignRowData } from '../utils';

export const useInfluencerProfileCampaignListTable = () => {
  const { t } = useTranslation();
  const { isTalentAgencyPartner } = useUserRoles();
  const { setPostDetailsParam } = usePostDetailsParam();

  const checkCanAccessMarketplace = (campaign: InfluencerProfileCampaignRowData['campaign']) =>
    !(campaign.type === CampaignType.MARKETPLACE && isTalentAgencyPartner) &&
    // no campaign ID means that current campaign is not associated with logged in user, so we should disable the links
    campaign.id;

  const columns: ColumnDef<InfluencerProfileCampaignRowData>[] = [
    {
      size: 264, // For multiple sticky columns
      accessorKey: 'campaign',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Campaign'),
      cell: ({ row }) => {
        const { campaign, showCampaignInfo } = row.original;

        return showCampaignInfo ? (
          <TableCell.Campaign
            type={campaign.type}
            src={campaign.thumbnail}
            id={campaign.id?.toString() || ''}
            isPostTracking={!!campaign.hasPostTracking}
            mkpCampaignType={campaign.mkpDetailType}
            title={campaign.title || ''}
            className={cn(
              'w-60 border-none bg-transparent p-0',
              checkCanAccessMarketplace(campaign) ? 'pointer-events-auto' : 'pointer-events-none'
            )}
          />
        ) : (
          <div />
        );
      }
    },
    {
      size: 250, // For multiple sticky columns
      accessorKey: 'content',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Content'),
      cell: ({ row }) => {
        const { post, campaign, numberOfPosts } = row.original;
        const canAccessMarketplace = checkCanAccessMarketplace(campaign);

        return post ? (
          !campaign.hasPostTracking ? (
            <span>
              {post.promotionMethods?.map((promoMethod) => t(`PromotionMethod.${promoMethod}`)).join(', ') || '-'}
            </span>
          ) : (
            <div className="flex items-center">
              <MediaPreview
                src={post.thumbnail}
                className="mr-2 size-12"
                onClick={() => (canAccessMarketplace ? setPostDetailsParam(post.id) : null)}
              />
              <div className="flex min-w-42.5 flex-col justify-center">
                <TextCutter
                  lines={2}
                  text={post.content}
                  onClick={() => (canAccessMarketplace ? setPostDetailsParam(post.id) : null)}
                />
                {post.url ? (
                  <Anchor variant="blue" href={post.url} target="_blank" className="mt-1.5 flex items-center text-sm">
                    <SocialIcon size="18px" className="mr-1" type={post.socialMedia as SocialIconProps['type']} />
                    {t('View on', { socialMedia: getSocialMediaTextLink(post.socialMedia) })}
                  </Anchor>
                ) : null}
              </div>
            </div>
          )
        ) : (
          <TableCell.Text
            value={
              numberOfPosts === 0 ? (
                t(campaign?.type === CampaignType.MARKETPLACE ? 'Joined the campaign' : 'Selected for the campaign')
              ) : (
                <>
                  <b>{numberOfPosts}</b>&nbsp;
                  {t('Posted')}
                </>
              )
            }
          />
        );
      }
    },
    {
      accessorKey: 'cost',
      header: t<string>('HeaderColumn.Influencer Cost'),
      meta: { align: 'right', sortField: InfluencerCampaignListSortField.COST },
      cell: ({ row }) => {
        const { summary, currency } = row.original;

        return <TableCell.Number value={formatPrice(summary.cost, currency)} unit={summary.cost ? t(currency) : ''} />;
      }
    },
    {
      accessorKey: 'engagement',
      header: t<string>('HeaderColumn.Engagement'),
      meta: { align: 'right', sortField: InfluencerCampaignListSortField.ENGAGEMENT },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.summary.engagement)} />
    },
    {
      accessorKey: 'engagementRate',
      header: t<string>('HeaderColumn.Engagement Rate'),
      meta: { align: 'right', sortField: InfluencerCampaignListSortField.ENGAGEMENT_RATE },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.summary.engagementRate, false)} unit="%" />
    },
    {
      accessorKey: 'like',
      header: t<string>('HeaderColumn.Like'),
      meta: { align: 'right', sortField: InfluencerCampaignListSortField.LIKE },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.summary.like)} />
    },
    {
      accessorKey: 'comment',
      header: t<string>('HeaderColumn.Comment'),
      meta: { align: 'right', sortField: InfluencerCampaignListSortField.COMMENT },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.summary.comment)} />
    },
    {
      accessorKey: 'share',
      header: t<string>('HeaderColumn.Share'),
      meta: { align: 'right', sortField: InfluencerCampaignListSortField.SHARE },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.summary.share)} />
    },
    {
      accessorKey: 'reach',
      header: t<string>('HeaderColumn.Reach'),
      meta: { align: 'right', sortField: InfluencerCampaignListSortField.REACH },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.summary.reach)} />
    },
    {
      accessorKey: 'click',
      header: t<string>('HeaderColumn.Click'),
      meta: { align: 'right', sortField: InfluencerCampaignListSortField.CLICK },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.summary.click)} />
    },
    {
      accessorKey: 'conversion',
      header: t<string>('HeaderColumn.Conversion'),
      meta: { align: 'right', sortField: InfluencerCampaignListSortField.CONVERSION },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.summary.conversion)} />
    },
    {
      accessorKey: 'cvr',
      header: t<string>('HeaderColumn.CV Rate'),
      meta: { align: 'right', sortField: InfluencerCampaignListSortField.CVR },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.summary.cvr, false)} unit="%" />
    },
    {
      accessorKey: 'joinedDate',
      header: t<string>('HeaderColumn.Joined Date'),
      meta: { sortField: InfluencerCampaignListSortField.JOINED_DATE },
      cell: ({ row }) => <TableCell.Date value={row.original.joinedDate} />
    }
  ];

  return { columns };
};
